<script setup lang="ts">
  import { onMounted, ref, computed } from 'vue'
  import { PhoneNumbersService, type CustomerPhoneNumber as PhoneNumber } from '@/types/generated'
  import { useRoute } from 'vue-router'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import * as yup from 'yup'
  import type { GenericObject } from 'vee-validate'
  import { useTranslation } from '@/composables/useTranslation'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import {
    ClipboardIcon,
    QuestionMarkCircleIcon,
    ChevronRightIcon
  } from '@heroicons/vue/24/outline'
  import { CheckIcon } from 'lucide-vue-next'
  import InputField from '@/components/admin/form/InputField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import { Button } from '@/components/ui/button'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

  const { t } = useTranslation({
    en: {
      active: 'Active',
      status: 'Status',
      statusDescription: 'Phone number availability',
      inactive: 'Inactive',

      phoneNumber: 'Phone number',
      copy: 'Copy',
      copied: 'Copied!',
      deactivationWarning:
        'Warning: Deactivating this phone number will deregister it from the phone provider. It will no longer be available for incoming calls. This action can not be undone.',
      inactiveInfo:
        'This phone number is inactive. Please contact support for more information or help.',
      description: 'Description',
      lawFirmName: 'Law Firm Name',
      address: 'Address',
      opening_hours: 'Opening Hours',
      lawyer_names: 'Lawyer Names',
      has_free_initial_consultation: 'Has free initial consultation',
      begin_message: 'Welcome message',
      forwarding_no_answer_message: 'Message for unanswered forwarded calls',
      end_message: 'Goodbye message',

      descriptionHint: 'Here you can store an internal designation for the phone number.',
      lawFirmNameHint:
        'Here you specify the name of the law firm as it should be mentioned in the greeting announcement.',
      originalPhoneNumberHint:
        'If you forward our generated number internally, please enter the original number here that clients call. This ensures we can correctly assign calls.',
      freeConsultationHint:
        'If a free initial consultation is offered, this should be activated here. The AI can then point out that the initial consultation is free.',
      addressHint:
        'Here you can specify the address of the law firm. We will pass this on to the caller upon request. If you do not want unannounced client visits, please provide a corresponding note here.',
      openingHoursHint:
        'Here you can enter the opening hours. These can be reproduced by the AI upon request.',
      lawyerNamesHint:
        "Here the names of the lawyers are entered. This helps the AI to better recognize them and distinguish them from the caller's name. Only enter the name here and no other designation such as lawyer or case worker.",
      beginMessageHint:
        'Here you can store a welcome message for the law firm. This will be played to the caller in advance before the call is answered.',
      forwardingNoAnswerMessageHint:
        'This message is played when a forwarded call goes unanswered.',
      endMessageHint:
        'Here you can store a farewell message for the law firm. This will be played to the caller in advance before the call is answered.',
      ai: 'Artificial Intelligence',
      phoneNumbers: 'Phone Numbers',
      enterYourAnswerHere: 'Enter your answer here...'
    },
    de: {
      active: 'Aktiv',
      status: 'Status',
      statusDescription: 'Telefonnummer Verfügbarkeit',
      inactive: 'Inaktiv',

      phoneNumber: 'Telefonnummer',
      copy: 'Kopieren',
      copied: 'Kopiert!',
      deactivationWarning:
        'Warnung: Durch das Deaktivieren dieser Telefonnummer wird sie vom Telefonanbieter abgemeldet. Sie ist nicht mehr für eingehende Anrufe verfügbar. Diese Aktion kann nicht rückgängig gemacht werden.',
      inactiveInfo:
        'Diese Telefonnummer ist inaktiv. Bitte kontaktieren Sie den Support für weitere Informationen oder Hilfe.',
      description: 'Beschreibung',
      lawFirmName: 'Kanzleiname',
      address: 'Adresse',
      opening_hours: 'Öffnungszeiten',
      lawyer_names: 'Anwälte',
      has_free_initial_consultation: 'Erstberatung',
      begin_message: 'Begrüßungsnachricht',
      forwarding_no_answer_message: 'Nachricht bei nicht beantworteter Weiterleitung',
      end_message: 'Abschiedsnachricht',

      descriptionHint: 'Hier kann eine interne Bezeichnung für die Rufnummer hinterlegt werden.',
      lawFirmNameHint:
        'Hier wird der Name der Kanzlei angegeben, wie er in der Begrüßungsansage genannt werden soll.',
      originalPhoneNumberHint:
        'Wenn Sie die von uns generierte Nummer intern weiterleiten, geben Sie hier unbedingt die Originalnummer an, welche von den Mandanten angerufen wird. So stellen Sie sicher, dass wir die Anrufe korrekt zuordnen können.',
      freeConsultationHint:
        'Wenn eine kostenlose Erstberatung angeboten wird, sollte das hier aktiviert werden. Die KI kann dann darauf hinweisen, dass die Erstberatung kostenlos ist.',
      addressHint:
        'Hier kann die Adresse der Kanzlei angegeben werden. Bei Nachfrage geben wir diese an den Anrufer weiter. Wenn Sie keine unangekündigten Mandantenbesuch wünschen, dann geben Sie hier bitte einen entsprechenden Hinweis.',
      openingHoursHint:
        'Hier können die Öffnungszeiten eingetragen werden. Diese können bei Nachfrage von der KI-Wiedergegeben werden.',
      lawyerNamesHint:
        'Hier werden die Namen der Anwälte eingetragen. Damit die KI diese besser erkennt und vom Anrufernamen unterscheiden kann. Hier nur den Namen eintragen und keine weitere Bezeichnung wie zum Beispiel Anwalt oder Sachbearbeiter.',
      beginMessageHint:
        'Hier kann eine Begrüßungsnachricht für die Kanzlei hinterlegt werden. Diese wird vorab an den Anrufer gespielt, bevor der Anruf angenommen wird.',
      forwardingNoAnswerMessageHint:
        'Diese Nachricht wird abgespielt, wenn eine weitergeleitete Nummer nicht abnimmt.',
      endMessageHint:
        'Hier kann eine Abschiedsnachricht für die Kanzlei hinterlegt werden. Diese wird am Schluss des Anrufes abgespielt, nachdem alle Informationen gesammelt wurden.',
      ai: 'Künstliche Intelligenz',
      phoneNumbers: 'Telefonnummern',
      enterYourAnswerHere: 'Antwort hier eingeben...'
    }
  })

  const route = useRoute()
  const phoneNumber = ref<PhoneNumber | null>(null)
  const defaultBeginMessage = computed(() => {
    if (!phoneNumber.value?.lawfirm_name) return ''
    return `Hallo, mein Name ist Marie und ich bin die KI Anrufannahme der ${phoneNumber.value.lawfirm_name}. Ich nehme gerne Ihr Anliegen auf und gebe es sofort an unser Team weiter, damit wir Ihnen schnellstmöglich weiterhelfen können.
Sie können mit mir in ganzen Sätzen wie mit einem Menschen sprechen.
Wobei können wir behilflich sein ?
    `
  })

  const defaultForwardingNoAnswerMessage =
    'Leider ist die Leitung aktuell belegt. Ich nehme aber gerne Ihr Anliegen sofort auf und Sie erhalten schnellstmöglich eine Rückmeldung.'

  onMounted(async () => {
    try {
      phoneNumber.value = (await PhoneNumbersService.phoneNumbersRetrieve({
        uuid: route.params.uuid.toString()
      })) as PhoneNumber
    } catch (error) {
      console.error('Error fetching phone number:', error)
    }
  })

  const handleSave = async (values: GenericObject) => {
    try {
      // Remove phone_number from values since it's read-only and shouldn't be updated
      const { phone_number, ...updateData } = values

      await PhoneNumbersService.phoneNumbersPartialUpdate({
        uuid: phoneNumber.value!.uuid!,
        requestBody: updateData
      })
    } catch (error) {
      console.error('Error updating phone number:', error)
    }
  }

  const copySuccess = ref(false)

  const copyPhoneNumberToClipboard = () => {
    if (phoneNumber.value?.phone_number) {
      navigator.clipboard.writeText(phoneNumber.value.phone_number)
      copySuccess.value = true
      setTimeout(() => {
        copySuccess.value = false
      }, 2000)
    }
  }
</script>

<template>
  <TooltipProvider>
    <SettingsPage>
      <template #header>
        <div class="flex items-center justify-between">
          <nav class="flex items-center gap-2 text-sm text-gray-500">
            <div>{{ t('ai') }}</div>
            <ChevronRightIcon class="size-3" />
            <div>{{ t('phoneNumbers') }}</div>
            <ChevronRightIcon class="size-3" />
            <span class="font-medium text-gray-900">{{ phoneNumber?.phone_number }}</span>
          </nav>
        </div>
      </template>

      <div
        v-if="phoneNumber"
        class="max-w-4xl space-y-6"
      >
        <!-- Inactive Status Warning -->
        <div
          v-if="!phoneNumber.active"
          class="rounded-lg border border-red-200 bg-red-50 p-4"
        >
          <div class="flex">
            <div class="flex-shrink-0">
              <svg
                class="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-red-700">
                {{ t('inactiveInfo') }}
              </p>
            </div>
          </div>
        </div>

        <AutoSavingForm
          class="flex flex-col gap-3"
          @save="handleSave"
        >
          <!-- Active Status Section -->
          <div class="mb-6">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-sm font-medium text-gray-900">{{ t('status') }}</h3>
                <p class="text-xs text-gray-500">{{ t('statusDescription') }}</p>
              </div>
              <div class="flex items-center gap-3">
                <div class="flex items-center gap-2">
                  <div
                    :class="[
                      'h-3 w-3 rounded-full',
                      phoneNumber.active ? 'bg-green-500' : 'bg-red-500'
                    ]"
                  ></div>
                  <span class="text-sm font-medium text-gray-900">
                    {{ phoneNumber.active ? t('active') : t('inactive') }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Phone Number Section -->
          <div class="mb-6">
            <div class="mb-2">
              <h3 class="text-sm font-medium text-gray-900">{{ t('phoneNumber') }}</h3>
            </div>
            <div class="relative">
              <InputField
                :model-value="phoneNumber.phone_number"
                name="phone_number"
                :rules="yup.string()"
                disabled
              />
              <Button
                v-if="!copySuccess"
                type="button"
                variant="ghost"
                size="sm"
                class="absolute right-3 top-1/2 h-8 w-8 -translate-y-1/2 p-0"
                @click="copyPhoneNumberToClipboard"
              >
                <ClipboardIcon class="size-4" />
              </Button>
              <!-- Copy success indicator -->
              <div
                v-if="copySuccess"
                class="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-1 rounded bg-green-100 px-2 py-1
                  text-xs text-green-700"
              >
                <CheckIcon class="h-3 w-3" />
                {{ t('copied') }}
              </div>
            </div>
          </div>

          <!-- Form Fields -->
          <div class="space-y-4">
            <!-- Description -->
            <div class="space-y-1">
              <div class="flex items-center gap-2">
                <label class="text-sm font-medium text-gray-900">{{ t('description') }}</label>
                <Tooltip>
                  <TooltipTrigger>
                    <QuestionMarkCircleIcon class="size-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent
                    variant="secondary"
                    showArrow
                    class="max-w-xs"
                  >
                    <p>{{ t('descriptionHint') }}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <InputField
                v-model="phoneNumber.description"
                name="description"
                :rules="yup.string()"
              />
            </div>

            <!-- Law Firm Name -->
            <div class="space-y-1">
              <div class="flex items-center gap-2">
                <label class="text-sm font-medium text-gray-900">{{ t('lawFirmName') }}</label>
                <Tooltip>
                  <TooltipTrigger>
                    <QuestionMarkCircleIcon class="size-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent
                    variant="secondary"
                    showArrow
                    class="max-w-xs"
                  >
                    <p>{{ t('lawFirmNameHint') }}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <InputField
                v-model="phoneNumber.lawfirm_name"
                name="lawfirm_name"
                :rules="yup.string()"
              />
            </div>

            <!-- Original Phone Number -->
            <div class="space-y-1">
              <div class="flex items-center gap-2">
                <label class="text-sm font-medium text-gray-900">
                  Original {{ t('phoneNumber') }}
                </label>
                <Tooltip>
                  <TooltipTrigger>
                    <QuestionMarkCircleIcon class="size-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent
                    variant="secondary"
                    showArrow
                    class="max-w-xs"
                  >
                    <p>{{ t('originalPhoneNumberHint') }}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <InputField
                v-model="phoneNumber.original_phone_number"
                name="original_phone_number"
                :rules="yup.string()"
              />
            </div>

            <!-- Free Consultation Checkbox -->
            <div class="rounded-lg bg-gray-50 p-3">
              <div class="flex items-start gap-3">
                <CheckboxField
                  v-model="phoneNumber.has_free_initial_consultation"
                  :label="t('has_free_initial_consultation')"
                  name="has_free_initial_consultation"
                />
                <div class="flex-1">
                  <p class="text-sm text-gray-600">{{ t('freeConsultationHint') }}</p>
                </div>
              </div>
            </div>

            <!-- Address -->
            <div class="space-y-1">
              <div class="flex items-center gap-2">
                <label class="text-sm font-medium text-gray-900">{{ t('address') }}</label>
                <Tooltip>
                  <TooltipTrigger>
                    <QuestionMarkCircleIcon class="size-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent
                    variant="secondary"
                    showArrow
                    class="max-w-xs"
                  >
                    <p>{{ t('addressHint') }}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <TextAreaField
                v-model="phoneNumber.address"
                name="address"
                :rules="yup.string()"
                :placeholder="t('enterYourAnswerHere')"
              />
            </div>

            <!-- Opening Hours -->
            <div class="space-y-1">
              <div class="flex items-center gap-2">
                <label class="text-sm font-medium text-gray-900">{{ t('opening_hours') }}</label>
                <Tooltip>
                  <TooltipTrigger>
                    <QuestionMarkCircleIcon class="size-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent
                    variant="secondary"
                    showArrow
                    class="max-w-xs"
                  >
                    <p>{{ t('openingHoursHint') }}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <TextAreaField
                v-model="phoneNumber.opening_hours"
                name="opening_hours"
                :rules="yup.string()"
                :placeholder="t('enterYourAnswerHere')"
              />
            </div>

            <!-- Lawyer Names -->
            <div class="space-y-1">
              <div class="flex items-center gap-2">
                <label class="text-sm font-medium text-gray-900">{{ t('lawyer_names') }}</label>
                <Tooltip>
                  <TooltipTrigger>
                    <QuestionMarkCircleIcon class="size-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent
                    variant="secondary"
                    showArrow
                    class="max-w-xs"
                  >
                    <p>{{ t('lawyerNamesHint') }}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <TextAreaField
                v-model="phoneNumber.lawyer_names"
                name="lawyer_names"
                :rules="yup.string()"
                :placeholder="t('enterYourAnswerHere')"
              />
            </div>

            <!-- Welcome Message -->
            <div class="space-y-1">
              <div class="flex items-center gap-2">
                <label class="text-sm font-medium text-gray-900">{{ t('begin_message') }}</label>
                <Tooltip>
                  <TooltipTrigger>
                    <QuestionMarkCircleIcon class="size-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent
                    variant="secondary"
                    showArrow
                    class="max-w-xs"
                  >
                    <p>{{ t('beginMessageHint') }}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <TextAreaField
                v-model="phoneNumber.begin_message"
                :value="phoneNumber.begin_message ?? defaultBeginMessage"
                name="begin_message"
                :rules="yup.string()"
                :placeholder="defaultBeginMessage"
                rows="5"
              />
            </div>

            <!-- Message for Unanswered Forwarded Calls -->
            <div class="space-y-1">
              <div class="flex items-center gap-2">
                <label class="text-sm font-medium text-gray-900">
                  {{ t('forwarding_no_answer_message') }}
                </label>
                <Tooltip>
                  <TooltipTrigger>
                    <QuestionMarkCircleIcon class="size-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent
                    variant="secondary"
                    showArrow
                    class="max-w-xs"
                  >
                    <p>{{ t('forwardingNoAnswerMessageHint') }}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <TextAreaField
                v-model="phoneNumber.forwarding_no_answer_message"
                :value="phoneNumber.forwarding_no_answer_message ?? ''"
                name="forwarding_no_answer_message"
                :rules="yup.string()"
                :placeholder="defaultForwardingNoAnswerMessage"
                rows="5"
              />
            </div>

            <!-- Goodbye Message -->
            <div class="space-y-1">
              <div class="flex items-center gap-2">
                <label class="text-sm font-medium text-gray-900">{{ t('end_message') }}</label>
                <Tooltip>
                  <TooltipTrigger>
                    <QuestionMarkCircleIcon class="size-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent
                    variant="secondary"
                    showArrow
                    class="max-w-xs"
                  >
                    <p>{{ t('endMessageHint') }}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <TextAreaField
                v-model="phoneNumber.end_message"
                :value="phoneNumber.end_message ?? ''"
                name="end_message"
                :rules="yup.string()"
                :placeholder="t('enterYourAnswerHere')"
                rows="5"
              />
            </div>
          </div>
        </AutoSavingForm>
      </div>
    </SettingsPage>
  </TooltipProvider>
</template>
